import { Grid, GridItem, HStack, Stack, Text } from '@chakra-ui/react';
import { djs } from '@duke-hq/djs';
import { RouterOutputs } from '@duke-hq/svc-api/admin';
import { ResourceStatusBadge } from '@duke-hq/ui/components/ResourceStatusBadge/ResourceStatusBadge';
import { Trans } from '@lingui/react/macro';
import { hasPlacementAgreement, PlacementAgreementLinks } from './PlacementAgreementLinks';
import { testIds } from './testIds';

type Placement =
  RouterOutputs['workPlacements']['placements']['journeyByEnrolment']['placements'][number];

function Field({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
  return (
    <Stack gap={1}>
      <Text fontSize="xs" color="fg.muted">
        {label}
      </Text>
      <Text as="div" fontSize="sm">
        {value}
      </Text>
    </Stack>
  );
}

function formatDate(date: Date | null): string {
  return date != null ? djs(date).format('DD MMM YYYY') : '—';
}

export function PlacementDetailsSection({
  placement,
  headerActions,
}: {
  placement: Pick<
    Placement,
    'startedAt' | 'endedAt' | 'notes' | 'units' | 'agreementFileDrns' | 'contractDocumentUrl'
  >;
  headerActions?: React.ReactNode;
}): React.ReactElement {
  return (
    <Stack
      bg="bg.surface"
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="2xl"
      p={5}
      gap={4}
      data-testid={testIds.placementDetailsSection}
    >
      <HStack justify="space-between" align="center" gap={3} flexWrap="wrap">
        <Text fontSize="lg" fontWeight="semibold">
          <Trans>Placement details</Trans>
        </Text>
        {headerActions}
      </HStack>

      <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
        <GridItem>
          <Field label={<Trans>Placement start</Trans>} value={formatDate(placement.startedAt)} />
        </GridItem>
        <GridItem>
          <Field label={<Trans>Placement end</Trans>} value={formatDate(placement.endedAt)} />
        </GridItem>
        <GridItem>
          <Field
            label={<Trans>Workplace agreement</Trans>}
            value={
              hasPlacementAgreement(placement) ? (
                <PlacementAgreementLinks placement={placement} />
              ) : (
                '—'
              )
            }
          />
        </GridItem>
      </Grid>

      <Field label={<Trans>Notes</Trans>} value={placement.notes ?? '—'} />

      {placement.units.length > 0 ? (
        <Stack gap={3} pt={4} borderTopWidth="1px" borderColor="border.default">
          <Text fontSize="md" fontWeight="semibold">
            <Trans>Units covered by this placement</Trans>
          </Text>

          <HStack justify="space-between" align="center" gap={4}>
            <Text fontSize="xs" color="fg.muted">
              <Trans>Unit</Trans>
            </Text>
            <Text fontSize="xs" color="fg.muted" flexShrink={0}>
              <Trans>Theory status</Trans>
            </Text>
          </HStack>

          {placement.units.map((unit) => (
            <HStack key={unit.unitOfCompetencyId} justify="space-between" align="start" gap={4}>
              <Stack gap={0}>
                <Text fontSize="sm" fontWeight="medium">
                  {unit.code}
                </Text>
                <Text fontSize="xs" color="fg.muted">
                  {unit.title}
                </Text>
              </Stack>
              {unit.theoryStatus != null ? (
                <ResourceStatusBadge status={unit.theoryStatus} />
              ) : (
                <Text fontSize="sm" color="fg.muted" flexShrink={0}>
                  —
                </Text>
              )}
            </HStack>
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
}
